import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import getIcon from "../../../../utils/Icon";
import { updateCategoryPosition } from "../../../../store/slices/promoFormSlice";
import tokenApi from "../../../../api/tokenApi";
import { motion, AnimatePresence } from "framer-motion";

const formatNumberShort = (n) => {
    const num = Number(n);
    if (isNaN(num)) return "0";
    if (num >= 1e9) return (num / 1e9).toFixed(1).replace(/\.0$/, "") + "B";
    if (num >= 1e6) return (num / 1e6).toFixed(1).replace(/\.0$/, "") + "M";
    if (num >= 1e3) return (num / 1e3).toFixed(1).replace(/\.0$/, "") + "K";
    return num.toString();
};

/**
 * Input, который:
 * - при фокусе показывает чистое число для редактирования
 * - при блюре форматирует только визуально, и вызывает onChange
 *   только если значение действительно изменилось
 */
function FormattedInput({ value, onChange, className }) {
    const [display, setDisplay] = useState(String(value));
    const [editing, setEditing] = useState(false);

    useEffect(() => {
        if (!editing) {
            setDisplay(formatNumberShort(value));
        }
    }, [value, editing]);

    return (
        <input
            className={className}
            value={display}
            onFocus={() => {
                setEditing(true);
                setDisplay(String(value));       // raw value для редактирования
            }}
            onBlur={() => {
                setEditing(false);
                const num = parseFloat(display);
                if (!isNaN(num) && num !== Number(value)) {
                    onChange(String(num));         // только если действительно изменилось
                }
                setDisplay(formatNumberShort(value)); // обратно в формат
            }}
            onChange={(e) => setDisplay(e.target.value)}
        />
    );
}

export default function PromoTable() {
    const dispatch = useDispatch();
    const token = useSelector((s) => s.promoForm.tokenType);
    const raw = useSelector((s) => s.promoForm.category.positions);
    const categoryName = useSelector((s) => s.promoForm.category.category_name);
    const totalAmount = useSelector((s) => s.promoForm.totalAmount);
    const prizeCount = useSelector((s) => s.promoForm.prizePlacesCount);
    const promoType = useSelector((state) => state.promoForm.promoType);

    const [tokenIconUrl, setTokenIconUrl] = useState("");
    const [tokenSymbol, setTokenSymbol] = useState("");
    const [showSymbolTooltip, setShowSymbolTooltip] = useState(false);
    const truncateSymbol = (sym) => (sym.length > 9 ? sym.slice(0, 6) + "..." : sym);

    // Загрузка логотипа и символа токена
    useEffect(() => {
        (async () => {
            if (!token || token.length < 10) return;
            try {
                const res = await tokenApi.getTokenByAddress(token);
                if (res) {
                    res.logoUrl && setTokenIconUrl(res.logoUrl);
                    res.symbol && setTokenSymbol(res.symbol.toUpperCase());
                }
            } catch (e) {
                console.warn("Ошибка загрузки токена:", e);
            }
        })();
    }, [token]);

    // Пересчёт позиций — только для крипто-промо (НЕ для onlyCrystals!)
    // Для onlyCrystals budget/dropPerWinner уже посчитаны при создании и хранятся в positions
    useEffect(() => {
        // Пропускаем пересчёт для onlyCrystals — у них бюджет фиксированный из defaultBudget
        if (promoType === "onlyCrystals") return;
        
        let accW = 0, accB = 0, n = raw.length;
        raw.forEach((p, i) => {
            const wp = p.winPercent ?? 0;
            const wantW = i < n - 1
                ? Math.floor((wp / 100) * prizeCount)
                : prizeCount - accW;
            accW += wantW;

            const bp = p.budgetPercent ?? 0;
            const wantB = i < n - 1
                ? +((bp / 100) * totalAmount).toFixed(2)
                : +(totalAmount - accB).toFixed(2);
            accB += wantB;

            const wantD = wantW > 0 ? +(wantB / wantW).toFixed(4) : 0;

            if (
                wantW !== p.winners ||
                wantB !== p.budget ||
                wantD !== p.dropPerWinner
            ) {
                dispatch(updateCategoryPosition({
                    index: i,
                    winners: wantW,
                    budget: wantB,
                    dropPerWinner: wantD,
                }));
            }
        });
    }, [prizeCount, totalAmount, dispatch, raw, promoType]);

    // redistribute и handleChange — только для крипто-промо
    const redistribute = (index, field, rawValue) => {
        // Для onlyCrystals бюджет фиксированный — не редактируем
        if (promoType === "onlyCrystals") return;
        
        let newVal = parseFloat(rawValue);
        if (isNaN(newVal)) return;

        const working = raw.map((p, i) => ({
            index: i,
            winners: p.winners ?? Math.floor(((p.winPercent ?? 0) / 100) * prizeCount),
            budget: p.budget ?? +(((p.budgetPercent ?? 0) / 100) * totalAmount).toFixed(2),
        }));
        const target = working[index];
        const others = working.filter((x) => x.index !== index);

        if (field === "winners") {
            newVal = Math.floor(Math.min(Math.max(0, newVal), prizeCount));
            const delta = newVal - target.winners;
            target.winners = newVal;
            if (delta > 0) {
                others.sort((a, b) => b.winners - a.winners);
                let rem = delta;
                for (const o of others) {
                    if (!rem) break;
                    const take = Math.min(o.winners, rem);
                    o.winners -= take;
                    rem -= take;
                }
            } else if (delta < 0) {
                const free = -delta;
                const sumO = others.reduce((s, o) => s + o.winners, 0) || 1;
                let given = 0;
                others.forEach((o, idx) => {
                    const inc = idx < others.length - 1
                        ? Math.floor((o.winners / sumO) * free)
                        : free - given;
                    o.winners += inc;
                    given += inc;
                });
            }
        }

        if (field === "budget") {
            newVal = Math.min(Math.max(0, newVal), totalAmount);
            const delta = newVal - target.budget;
            target.budget = newVal;
            if (delta > 0) {
                others.sort((a, b) => b.budget - a.budget);
                let rem = delta;
                for (const o of others) {
                    if (!rem) break;
                    const take = Math.min(o.budget, rem);
                    o.budget -= take;
                    rem -= take;
                }
            } else if (delta < 0) {
                const free = -delta;
                const sumO = others.reduce((s, o) => s + o.budget, 0) || 1;
                let given = 0;
                others.forEach((o, idx) => {
                    const inc = idx < others.length - 1
                        ? Math.floor((o.budget / sumO) * free)
                        : free - given;
                    o.budget += inc;
                    given += inc;
                });
            }
        }

        working.forEach((p) => {
            const wp = +((p.winners / prizeCount) * 100).toFixed(2);
            const bp = +((p.budget / totalAmount) * 100).toFixed(2);
            const dp = p.winners ? +(p.budget / p.winners).toFixed(4) : 0;
            dispatch(updateCategoryPosition({
                index: p.index,
                winners: p.winners,
                winPercent: wp,
                budget: p.budget,
                budgetPercent: bp,
                dropPerWinner: dp,
            }));
        });
    };

    const handleChange = (i, field, v) => {
        // Для onlyCrystals бюджет фиксированный — не редактируем
        if (promoType === "onlyCrystals") return;
        
        if (field === "winners" || field === "budget") {
            redistribute(i, field, v);
            return;
        }
        const newVal = parseFloat(v);
        if (isNaN(newVal)) return;
        const others = raw.filter((_, j) => j !== i);
        const pos = raw[i];
        const upd = { index: i, field };

        if (field === "winPercent") {
            const sumO = others.reduce((s, p) => s + (p.winPercent ?? 0), 0);
            const val = Math.min(Math.max(0, newVal), 100 - sumO);
            upd.value = val;
            upd.winners = Math.floor((val / 100) * prizeCount);
            upd.dropPerWinner = upd.winners ? +(pos.budget / upd.winners).toFixed(4) : 0;
        } else if (field === "budgetPercent") {
            const sumO = others.reduce((s, p) => s + (p.budgetPercent ?? 0), 0);
            const val = Math.min(Math.max(0, newVal), 100 - sumO);
            upd.value = val;
            upd.budget = +((val / 100) * totalAmount).toFixed(2);
            upd.dropPerWinner = pos.winners ? +(((val / 100) * totalAmount) / pos.winners).toFixed(4) : 0;
        }

        dispatch(updateCategoryPosition(upd));
    };

    // Подготовка данных для рендера
    const positions = raw.map((p) => ({
        winPercent: +(p.winPercent ?? 0).toFixed(2),
        winners: p.winners ?? 0,
        budgetPercent: +(p.budgetPercent ?? 0).toFixed(2),
        budget: +(p.budget ?? 0).toFixed(2),
        dropPerWinner: +(p.dropPerWinner ?? 0).toFixed(4),
    }));

    const sumWP = positions.reduce((s, p) => s + p.winPercent, 0).toFixed(2);
    const sumW = positions.reduce((s, p) => s + p.winners, 0);
    const sumBP = positions.reduce((s, p) => s + p.budgetPercent, 0).toFixed(2);
    
    // Для onlyCrystals — бюджет из totalAmount (defaultBudget) или сумма positions
    const sumB = promoType === "onlyCrystals" 
        ? (totalAmount > 0 ? totalAmount : raw.reduce((sum, p) => {
            const budget = Number(p?.budget);
            if (!Number.isNaN(budget) && budget > 0) return sum + budget;
            const winners = Number(p?.winners) || 0;
            const drop = Number(p?.dropPerWinner) || 0;
            return sum + (winners * drop);
        }, 0)).toFixed(2)
        : positions.reduce((s, p) => s + p.budget, 0).toFixed(2);

    // Иконки категорий
    const iconMap = {
        3: ["star_big", "rectangle", "polygon"],
        5: ["crown", "plus", "star_big", "rectangle", "polygon"],
        7: ["diamond", "star", "crown", "plus", "star_big", "rectangle", "polygon"],
    };
    const colors = [['D240D2','F11E25','47C5D5','26EA5A','FFB700','808080','894F24'].reverse()];
    const num = parseInt(categoryName || "", 10) || 0;
    const icons = iconMap[num] || [];

    return (
        <div className="w-[450px] overflow-hidden rounded-xl border border-white/10 bg-white/[0.03] font-light text-white backdrop-blur-sm">
            {/* Header */}
            <div className="grid grid-cols-6 gap-4 border-b border-white/10 px-2 py-3 text-[13px] text-white/45">
                <div>category</div>
                <div className="text-right">win %</div>
                <div className="text-right">winners</div>
                <div className="text-right">{promoType === "onlyCrystals" ? "crystals %" : "budget %"}</div>
                <div className="text-right">{promoType === "onlyCrystals" ? "crystals" : "budget"}</div>
                <div className="text-center">reward</div>
            </div>

            {/* Rows */}
            {positions.map((it, idx) => (
                <div
                    key={idx}
                    className="grid h-[45px] grid-cols-6 items-center gap-4 border-b border-white/[0.08] px-2 text-[13px]"
                >
                    {/* category icon */}
                    <div className="flex justify-center">
                        {promoType === "cryptoOnly" ? (
                            <div
                                className="w-7 h-7 flex items-center justify-center rounded-full text-[13px] font-bold"
                                style={{
                                    border: `2px solid #${colors[0][(raw.length - 1 - idx) % colors[0].length]}`,
                                    color: `#${colors[0][(raw.length - 1 - idx) % colors[0].length]}`,
                                    backgroundColor: "transparent",
                                }}
                            >
                                {raw.length - idx}
                            </div>
                        ) : (
                            <img
                                src={getIcon(icons[idx] || "star_big")}
                                alt=""
                                className="w-7 h-7 object-contain"
                                style={{ transform: "scale(1.4)" }}
                            />
                        )}
                    </div>

                    {/* winPercent, winners, budgetPercent, budget */}
                    {["winPercent", "winners", "budgetPercent", "budget"].map((f) => (
                        <div key={f} className="text-right text-white/80">
                            {f === "winners" ? (
                                // Для onlyCrystals — readonly, для остальных — редактируемый
                                promoType === "onlyCrystals" ? (
                                    formatNumberShort(raw[idx].winners ?? it.winners)
                                ) : (
                                    <FormattedInput
                                        value={raw[idx].winners ?? it.winners}
                                        onChange={(v) => handleChange(idx, f, v)}
                                        className="w-[55px] bg-transparent text-white text-[13px] text-right outline-none"
                                    />
                                )
                            ) : f === "budget" ? (
                                // Для onlyCrystals — readonly, для остальных — редактируемый
                                promoType === "onlyCrystals" ? (
                                    formatNumberShort(raw[idx].budget ?? it.budget)
                                ) : (
                                    <FormattedInput
                                        value={raw[idx].budget ?? it.budget}
                                        onChange={(v) => handleChange(idx, f, v)}
                                        className="w-[55px] bg-transparent text-white text-[13px] text-right outline-none"
                                    />
                                )
                            ) : f === "winPercent" ? (
                                `${it.winPercent}%`
                            ) : (
                                `${it.budgetPercent}%`
                            )}
                        </div>
                    ))}

                    {/* dropPerWinner */}
                    <div className="flex items-center justify-center gap-1 border-l border-white/10 bg-amber-950/35 font-medium text-amber-200/95">
                        {formatNumberShort(it.dropPerWinner)}
                    </div>
                </div>
            ))}

            {/* Footer */}
            <div className="grid grid-cols-6 px-4 gap-4 mt-2 mb-2 text-[13px] font-medium">
                <div className="text-center">Total</div>
                <div className="text-right">{`${sumWP}%`}</div>
                <div className="text-right">{formatNumberShort(sumW)}</div>
                <div className="text-right">{`${sumBP}%`}</div>
                <div className="text-right">{formatNumberShort(sumB)}</div>
                <div className="flex items-center justify-center h-full">
                    {promoType === "onlyCrystals" ? (
                        <>
                            <span className="text-[9px] font-bold mr-1 text-purple-400">Crystals</span>
                            <img
                                src={getIcon(icons[0] || 'star_big')}
                                alt="crystal"
                                className="w-3 h-3 object-contain"
                                style={{ transform: "scale(1.4)" }}
                            />
                        </>
                    ) : (
                        <>
                            <div
                                className="relative cursor-pointer"
                                onClick={() => setShowSymbolTooltip(!showSymbolTooltip)}
                            >
                                <span className="text-[9px] font-bold mr-1">
                                    {truncateSymbol(tokenSymbol)}
                                </span>
                                <AnimatePresence>
                                    {showSymbolTooltip && tokenSymbol.length > 9 && (
                                        <motion.div
                                            initial={{ opacity: 0, y: -4 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: -4 }}
                                            transition={{ duration: 0.2 }}
                                            className="absolute left-1/2 top-[120%] z-20 -translate-x-1/2 whitespace-nowrap rounded-lg border border-white/10 bg-[#0f1419]/98 px-2 py-1 text-[11px] text-white shadow-lg backdrop-blur-md"
                                        >
                                            {tokenSymbol}
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                            <img
                                src={tokenIconUrl}
                                alt=""
                                className="w-3 h-3 object-contain rounded-full"
                                style={{ transform: "scale(1.4)" }}
                            />
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
